/*
  COMPONENT: Product Gallery Runtime

  Owns:
  - Product media thumbnail selection
  - Active media state

  Does not own:
  - Product variant selection
  - Product media rendering markup
*/

(() => {
  const zoomStates = new WeakMap();

  function getZoomState(gallery) {
    if (!zoomStates.has(gallery)) {
      zoomStates.set(gallery, {
        scale: 1,
        panX: 0,
        panY: 0,
        dragStartX: 0,
        dragStartY: 0,
        startPanX: 0,
        startPanY: 0,
        dragging: false,
        pinchDistance: 0,
        pinchScale: 1,
        pointers: new Map(),
        swipeStartX: 0,
        swipeStartY: 0,
        suppressClick: false,
      });
    }
    return zoomStates.get(gallery);
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function getItems(gallery) {
    return Array.from(gallery.querySelectorAll('[data-gallery-media]'));
  }

  function getActiveItem(gallery) {
    return gallery.querySelector('[data-gallery-media].is-active') || gallery.querySelector('[data-gallery-media]:not([hidden])');
  }

  function getActiveIndex(gallery) {
    const items = getItems(gallery);
    const active = getActiveItem(gallery);
    return Math.max(0, items.indexOf(active));
  }

  function syncGalleryVideos(items, activeItem, playActive = true) {
    items.forEach((item) => {
      item.querySelectorAll('video').forEach((video) => {
        video.muted = false;
        video.playsInline = true;
        if (item === activeItem && playActive) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    });
  }

  function setActive(gallery, mediaId) {
    const items = getItems(gallery);
    const activeIndex = items.findIndex((item) => item.dataset.galleryMedia === mediaId);
    const nextIndex = activeIndex >= 0 ? activeIndex : 0;
    const nextItem = items[nextIndex];
    if (!nextItem) return;

    items.forEach((item) => {
      const active = item.dataset.galleryMedia === mediaId;
      item.classList.toggle('is-active', active);
      item.toggleAttribute('hidden', !active);
    });

    gallery.querySelectorAll('[data-gallery-thumb]').forEach((button) => {
      const active = button.dataset.galleryThumb === mediaId;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-current', active ? 'true' : 'false');
    });

    gallery.querySelectorAll('[data-gallery-progress]').forEach((item) => {
      item.classList.toggle('is-active', item.dataset.galleryProgress === mediaId);
    });

    const counter = gallery.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = `${nextIndex + 1} / ${items.length}`;

    const zoomButton = gallery.querySelector('[data-gallery-zoom]');
    if (zoomButton) {
      const canZoom = nextItem.dataset.galleryType === 'image' && nextItem.dataset.galleryZoomSrc;
      zoomButton.toggleAttribute('hidden', !canZoom);
    }

    const magnifier = gallery.querySelector('[data-gallery-magnifier]');
    const mediaStage = gallery.querySelector('.main-product__media-stage');
    const videoActive = nextItem.dataset.galleryType === 'video' || nextItem.dataset.galleryType === 'external_video';
    mediaStage?.classList.toggle('is-video-active', videoActive);
    if (magnifier) {
      if (nextItem.dataset.galleryType === 'image' && nextItem.dataset.galleryZoomSrc) {
        magnifier.style.setProperty('--gallery-lens-image', `url("${nextItem.dataset.galleryZoomSrc}")`);
      } else {
        magnifier.style.removeProperty('--gallery-lens-image');
        mediaStage?.classList.remove('is-magnifying');
      }
    }

    const lightbox = gallery.querySelector('[data-gallery-lightbox]');
    syncGalleryVideos(items, nextItem, !lightbox || lightbox.hidden);
    resetZoom(gallery);
  }

  function setActiveByOffset(gallery, offset) {
    const items = getItems(gallery);
    if (items.length < 2) return;
    const nextIndex = (getActiveIndex(gallery) + offset + items.length) % items.length;
    setActive(gallery, items[nextIndex].dataset.galleryMedia);
  }

  function clearLightboxVideo(gallery) {
    const videoHost = gallery.querySelector('[data-gallery-lightbox-video]');
    if (!videoHost) return;

    videoHost.querySelectorAll('video').forEach((video) => {
      video.pause();
      video.removeAttribute('src');
      video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
      video.load();
    });
    videoHost.replaceChildren();
    videoHost.hidden = true;
  }

  function setLightboxZoomAvailability(gallery, available) {
    gallery.querySelectorAll('[data-gallery-zoom-out], [data-gallery-zoom-reset], [data-gallery-zoom-in]').forEach((control) => {
      control.hidden = !available;
    });
  }

  function renderLightboxVideo(gallery, item) {
    const sourceVideo = item.querySelector('video');
    const videoHost = gallery.querySelector('[data-gallery-lightbox-video]');
    if (!sourceVideo || !videoHost) return false;

    const lightboxVideo = sourceVideo.cloneNode(true);
    [lightboxVideo, ...lightboxVideo.querySelectorAll('[id]')].forEach((element) => element.removeAttribute('id'));
    lightboxVideo.classList.add('main-product__lightbox-video-player');
    lightboxVideo.controls = true;
    lightboxVideo.autoplay = true;
    lightboxVideo.playsInline = true;
    lightboxVideo.muted = sourceVideo.muted;
    lightboxVideo.removeAttribute('hidden');

    const resumeAt = Number.isFinite(sourceVideo.currentTime) ? sourceVideo.currentTime : 0;
    sourceVideo.pause();
    videoHost.replaceChildren(lightboxVideo);
    videoHost.hidden = false;
    lightboxVideo.load();

    const seekToResumePoint = () => {
      if (resumeAt > 0 && Number.isFinite(lightboxVideo.duration)) {
        lightboxVideo.currentTime = Math.min(resumeAt, Math.max(0, lightboxVideo.duration - 0.1));
      }
    };

    if (lightboxVideo.readyState >= 1) {
      seekToResumePoint();
    } else {
      lightboxVideo.addEventListener('loadedmetadata', seekToResumePoint, { once: true });
    }
    lightboxVideo.play().catch(() => {
      lightboxVideo.muted = true;
      lightboxVideo.play().catch(() => {});
    });
    return true;
  }

  function updateLightbox(gallery) {
    const lightbox = gallery.querySelector('[data-gallery-lightbox]');
    const image = gallery.querySelector('[data-gallery-lightbox-image]');
    if (!lightbox || !image || lightbox.hidden) return;

    const item = getActiveItem(gallery);
    const pan = gallery.querySelector('[data-gallery-lightbox-pan]');
    if (!item || !pan) return;

    const isImage = item.dataset.galleryType === 'image' && Boolean(item.dataset.galleryZoomSrc);
    const isVideo = item.dataset.galleryType === 'video';
    clearLightboxVideo(gallery);
    resetZoom(gallery);
    setLightboxZoomAvailability(gallery, isImage);
    pan.classList.toggle('is-video-active', isVideo);

    if (isImage) {
      image.hidden = false;
      image.src = item.dataset.galleryZoomSrc;
      image.alt = item.dataset.galleryAlt || '';
    } else if (isVideo && renderLightboxVideo(gallery, item)) {
      image.hidden = true;
      image.removeAttribute('src');
      image.alt = '';
    } else {
      return;
    }

    const caption = gallery.querySelector('[data-gallery-lightbox-caption]');
    if (caption) caption.textContent = item.dataset.galleryAlt || '';
  }

  function openLightbox(gallery) {
    const item = getActiveItem(gallery);
    const lightbox = gallery.querySelector('[data-gallery-lightbox]');
    if (!item?.dataset.galleryZoomSrc || !lightbox) return;

    lightbox.hidden = false;
    document.documentElement.classList.add('overflow-hidden');
    updateLightbox(gallery);
    gallery.querySelector('[data-gallery-lightbox-close]')?.focus();
  }

  function closeLightbox(gallery) {
    const lightbox = gallery.querySelector('[data-gallery-lightbox]');
    if (!lightbox || lightbox.hidden) return;

    lightbox.hidden = true;
    document.documentElement.classList.remove('overflow-hidden');
    if (document.fullscreenElement === lightbox) document.exitFullscreen?.();
    clearLightboxVideo(gallery);
    setLightboxZoomAvailability(gallery, true);
    resetZoom(gallery);
    const activeItem = getActiveItem(gallery);
    syncGalleryVideos(getItems(gallery), activeItem);
    const zoomButton = gallery.querySelector('[data-gallery-zoom]');
    if (zoomButton && !zoomButton.hidden) {
      zoomButton.focus();
    } else {
      Array.from(gallery.querySelectorAll('[data-gallery-thumb]'))
        .find((button) => button.dataset.galleryThumb === activeItem?.dataset.galleryMedia)
        ?.focus();
    }
  }

  function applyZoom(gallery) {
    const state = getZoomState(gallery);
    const wrap = gallery.querySelector('[data-gallery-lightbox-pan]');
    if (!wrap) return;

    if (state.scale <= 1) {
      state.panX = 0;
      state.panY = 0;
    }

    wrap.style.setProperty('--lightbox-scale', state.scale.toFixed(2));
    wrap.style.setProperty('--lightbox-pan-x', `${state.panX}px`);
    wrap.style.setProperty('--lightbox-pan-y', `${state.panY}px`);
    wrap.classList.toggle('is-zoomed', state.scale > 1);
  }

  function setZoom(gallery, scale, originX = 0, originY = 0) {
    const state = getZoomState(gallery);
    const previousScale = state.scale;
    state.scale = clamp(scale, 1, 4);

    if (state.scale > 1 && previousScale > 0 && (originX || originY)) {
      const ratio = state.scale / previousScale;
      state.panX = originX - (originX - state.panX) * ratio;
      state.panY = originY - (originY - state.panY) * ratio;
    }

    const maxPan = 420 * (state.scale - 1);
    state.panX = clamp(state.panX, -maxPan, maxPan);
    state.panY = clamp(state.panY, -maxPan, maxPan);
    applyZoom(gallery);
  }

  function resetZoom(gallery) {
    const state = getZoomState(gallery);
    state.scale = 1;
    state.panX = 0;
    state.panY = 0;
    state.dragging = false;
    state.pinchDistance = 0;
    state.pinchScale = 1;
    state.swipeStartX = 0;
    state.swipeStartY = 0;
    state.suppressClick = false;
    state.pointers.clear();
    gallery.querySelector('[data-gallery-lightbox-pan]')?.classList.remove('is-dragging');
    applyZoom(gallery);
  }

  function zoomBy(gallery, amount) {
    if (getActiveItem(gallery)?.dataset.galleryType !== 'image') return;
    const state = getZoomState(gallery);
    setZoom(gallery, state.scale + amount);
  }

  function pointerDistance(points) {
    if (points.length < 2) return 0;
    return Math.hypot(points[0].clientX - points[1].clientX, points[0].clientY - points[1].clientY);
  }

  function toggleFullscreen(gallery) {
    const lightbox = gallery.querySelector('[data-gallery-lightbox]');
    if (!lightbox) return;
    if (document.fullscreenElement === lightbox) {
      document.exitFullscreen?.();
    } else {
      lightbox.requestFullscreen?.();
    }
  }

  function getLightboxFocusable(lightbox) {
    return Array.from(lightbox.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.disabled && element.offsetParent !== null);
  }

  function initializeProductGalleries(root = document) {
    root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.initialized === 'true') return;
      gallery.dataset.initialized = 'true';

      const first = gallery.querySelector('[data-gallery-media]');
      if (first) setActive(gallery, first.dataset.galleryMedia);

      const mediaSwipe = {
        pointerId: null,
        startX: 0,
        startY: 0,
        suppressClick: false,
      };

      gallery.addEventListener('click', (event) => {
        if (mediaSwipe.suppressClick && event.target.closest('.main-product__media-stage')) {
          event.preventDefault();
          mediaSwipe.suppressClick = false;
          return;
        }

        const button = event.target.closest('[data-gallery-thumb]');
        if (button) {
          setActive(gallery, button.dataset.galleryThumb);
          return;
        }

        if (event.target.closest('[data-gallery-prev]')) {
          setActiveByOffset(gallery, -1);
          return;
        }

        if (event.target.closest('[data-gallery-next]')) {
          setActiveByOffset(gallery, 1);
          return;
        }

        if (event.target.closest('[data-gallery-zoom]')) {
          openLightbox(gallery);
          return;
        }

        const clickedMedia = event.target.closest('[data-gallery-media]');
        if (clickedMedia) {
          if (clickedMedia.dataset.galleryType === 'image') openLightbox(gallery);
          return;
        }

        if (event.target.closest('[data-gallery-lightbox-close]')) {
          closeLightbox(gallery);
          return;
        }

        if (event.target.closest('[data-gallery-lightbox-prev]')) {
          setActiveByOffset(gallery, -1);
          updateLightbox(gallery);
          return;
        }

        if (event.target.closest('[data-gallery-lightbox-next]')) {
          setActiveByOffset(gallery, 1);
          updateLightbox(gallery);
          return;
        }

        if (event.target.closest('[data-gallery-zoom-out]')) {
          zoomBy(gallery, -0.5);
          return;
        }

        if (event.target.closest('[data-gallery-zoom-in]')) {
          zoomBy(gallery, 0.5);
          return;
        }

        if (event.target.closest('[data-gallery-zoom-reset]')) {
          resetZoom(gallery);
          return;
        }

        if (event.target.closest('[data-gallery-fullscreen]')) {
          toggleFullscreen(gallery);
          return;
        }

        if (event.target.closest('[data-gallery-lightbox-pan]')) {
          if (getActiveItem(gallery)?.dataset.galleryType !== 'image') return;
          const state = getZoomState(gallery);
          if (state.suppressClick) {
            state.suppressClick = false;
            return;
          }
          setZoom(gallery, state.scale > 1 ? 1 : 2);
          return;
        }

        const lightbox = gallery.querySelector('[data-gallery-lightbox]');
        if (event.target === lightbox) closeLightbox(gallery);
      });

      gallery.addEventListener('keydown', (event) => {
        const lightbox = gallery.querySelector('[data-gallery-lightbox]');
        if (lightbox && !lightbox.hidden && event.key === 'Tab') {
          const focusable = getLightboxFocusable(lightbox);
          if (focusable.length) {
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first.focus();
            }
          }
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          setActiveByOffset(gallery, -1);
          updateLightbox(gallery);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          setActiveByOffset(gallery, 1);
          updateLightbox(gallery);
        } else if (event.key === 'Escape') {
          closeLightbox(gallery);
        } else if (event.key === '+' || event.key === '=') {
          zoomBy(gallery, 0.5);
        } else if (event.key === '-') {
          zoomBy(gallery, -0.5);
        } else if (event.key === '0') {
          resetZoom(gallery);
        }
      });

      const mediaStage = gallery.querySelector('.main-product__media-stage');
      const magnifier = gallery.querySelector('[data-gallery-magnifier]');

      mediaStage?.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') return;
        if (event.target.closest('button, [data-gallery-lightbox-pan]')) return;
        mediaSwipe.pointerId = event.pointerId;
        mediaSwipe.startX = event.clientX;
        mediaSwipe.startY = event.clientY;
        mediaStage.setPointerCapture?.(event.pointerId);
      });

      mediaStage?.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'mouse') return;
        if (mediaSwipe.pointerId !== event.pointerId) return;
        const deltaX = event.clientX - mediaSwipe.startX;
        const deltaY = event.clientY - mediaSwipe.startY;
        if (Math.abs(deltaX) <= 12 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.05) return;
        event.preventDefault();
      });

      ['pointerup', 'pointercancel'].forEach((eventName) => {
        mediaStage?.addEventListener(eventName, (event) => {
          if (event.pointerType === 'mouse') return;
          if (mediaSwipe.pointerId !== event.pointerId) return;
          const deltaX = event.clientX - mediaSwipe.startX;
          const deltaY = event.clientY - mediaSwipe.startY;
          if (mediaStage.hasPointerCapture?.(event.pointerId)) {
            mediaStage.releasePointerCapture(event.pointerId);
          }
          mediaSwipe.pointerId = null;

          if (eventName === 'pointercancel') return;
          if (Math.abs(deltaX) < 42 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.12) return;

          event.preventDefault();
          setActiveByOffset(gallery, deltaX > 0 ? -1 : 1);
          mediaSwipe.suppressClick = true;
          window.setTimeout(() => {
            mediaSwipe.suppressClick = false;
          }, 360);
        });
      });

      mediaStage?.addEventListener('pointerenter', () => {
        const active = getActiveItem(gallery);
        mediaStage.classList.toggle('is-magnifying', active?.dataset.galleryType === 'image');
      });

      mediaStage?.addEventListener('pointerleave', () => {
        mediaStage.classList.remove('is-magnifying');
      });

      mediaStage?.addEventListener('pointermove', (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        event.currentTarget.style.setProperty('--gallery-spot-x', `${x}%`);
        event.currentTarget.style.setProperty('--gallery-spot-y', `${y}%`);
        if (magnifier) {
          const active = getActiveItem(gallery);
          const activeImage = active?.querySelector('img.product-media__item, .product-media__item img, img');
          if (!active || active.dataset.galleryType !== 'image' || !activeImage) {
            mediaStage.classList.remove('is-magnifying');
            return;
          }

          const imageRect = activeImage.getBoundingClientRect();
          const insideImage =
            event.clientX >= imageRect.left &&
            event.clientX <= imageRect.right &&
            event.clientY >= imageRect.top &&
            event.clientY <= imageRect.bottom;

          mediaStage.classList.toggle('is-magnifying', insideImage);
          if (!insideImage) return;

          const lensZoom = 2.35;
          const lensSize = magnifier.getBoundingClientRect().width || 160;
          const imageX = event.clientX - imageRect.left;
          const imageY = event.clientY - imageRect.top;
          const backgroundX = lensSize / 2 - imageX * lensZoom;
          const backgroundY = lensSize / 2 - imageY * lensZoom;

          magnifier.style.left = `${event.clientX - rect.left}px`;
          magnifier.style.top = `${event.clientY - rect.top}px`;
          magnifier.style.setProperty('--gallery-lens-width', `${imageRect.width * lensZoom}px`);
          magnifier.style.setProperty('--gallery-lens-height', `${imageRect.height * lensZoom}px`);
          magnifier.style.setProperty('--gallery-lens-x', `${backgroundX}px`);
          magnifier.style.setProperty('--gallery-lens-y', `${backgroundY}px`);
        }
      });

      const lightboxPan = gallery.querySelector('[data-gallery-lightbox-pan]');

      lightboxPan?.addEventListener('wheel', (event) => {
        if (getActiveItem(gallery)?.dataset.galleryType !== 'image') return;
        event.preventDefault();
        const rect = lightboxPan.getBoundingClientRect();
        const originX = event.clientX - rect.left - rect.width / 2;
        const originY = event.clientY - rect.top - rect.height / 2;
        const state = getZoomState(gallery);
        setZoom(gallery, state.scale + (event.deltaY < 0 ? 0.28 : -0.28), originX, originY);
      });

      lightboxPan?.addEventListener('pointerdown', (event) => {
        if (getActiveItem(gallery)?.dataset.galleryType !== 'image') return;
        const state = getZoomState(gallery);
        state.pointers.set(event.pointerId, event);
        lightboxPan.setPointerCapture?.(event.pointerId);
        state.swipeStartX = event.clientX;
        state.swipeStartY = event.clientY;

        if (state.pointers.size === 2) {
          const points = Array.from(state.pointers.values());
          state.pinchDistance = pointerDistance(points);
          state.pinchScale = state.scale;
          return;
        }

        if (state.scale <= 1) return;
        state.dragging = true;
        state.dragStartX = event.clientX;
        state.dragStartY = event.clientY;
        state.startPanX = state.panX;
        state.startPanY = state.panY;
        lightboxPan.classList.add('is-dragging');
      });

      lightboxPan?.addEventListener('pointermove', (event) => {
        if (getActiveItem(gallery)?.dataset.galleryType !== 'image') return;
        const state = getZoomState(gallery);
        if (state.pointers.has(event.pointerId)) state.pointers.set(event.pointerId, event);

        if (state.pointers.size === 2 && state.pinchDistance > 0) {
          const points = Array.from(state.pointers.values());
          const distance = pointerDistance(points);
          setZoom(gallery, state.pinchScale * (distance / state.pinchDistance));
          state.suppressClick = true;
          return;
        }

        if (!state.dragging) return;
        state.panX = state.startPanX + event.clientX - state.dragStartX;
        state.panY = state.startPanY + event.clientY - state.dragStartY;
        setZoom(gallery, state.scale);
        state.suppressClick = true;
      });

      ['pointerup', 'pointercancel'].forEach((eventName) => {
        lightboxPan?.addEventListener(eventName, (event) => {
          if (getActiveItem(gallery)?.dataset.galleryType !== 'image') return;
          const state = getZoomState(gallery);
          const deltaX = event.clientX - state.swipeStartX;
          const deltaY = event.clientY - state.swipeStartY;
          state.pointers.delete(event.pointerId);
          if (state.pointers.size < 2) {
            state.pinchDistance = 0;
            state.pinchScale = state.scale;
          }
          state.dragging = false;
          lightboxPan.classList.remove('is-dragging');
          if (lightboxPan.hasPointerCapture?.(event.pointerId)) {
            lightboxPan.releasePointerCapture(event.pointerId);
          }
          if (state.scale <= 1 && Math.abs(deltaX) > 42 && Math.abs(deltaX) > Math.abs(deltaY) * 1.12) {
            if (event.pointerType === 'mouse') return;
            event.preventDefault();
            setActiveByOffset(gallery, deltaX > 0 ? -1 : 1);
            updateLightbox(gallery);
            getZoomState(gallery).suppressClick = true;
          }
        });
      });

      gallery.closest('[data-main-product]')?.addEventListener('product:variant-change', (event) => {
        if (!event.detail?.mediaId) return;
        setActive(gallery, event.detail.mediaId);
      });
    });
  }

  initializeProductGalleries();
  document.addEventListener('shopify:section:load', (event) => initializeProductGalleries(event.target));
})();
